import React, { useEffect, useRef, useState } from "react";
import { Grid, Typography, Modal, Box, Rating, Button, CircularProgress } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import OrderSummary from "../../components/createSecondaryOrder/OrderSummary";
import OrderDetails from "../../components/createSecondaryOrder/OrderDetails";
import success from "../../assets/gif/success.gif";
import FormHeader from "../dms_common/FormHeader";
import { ReactComponent as PercentIcon } from "../../assets/image/svg/percentageIconBlue.svg";
import { ChannelkartNetworkPost, OrderRatingPost } from "../../services/networkService";
import CartSummaryFooter from "../dms_common/CartSummaryFooter";
import { getActivityReminderPayload } from "./util";
import { clearAllDataFromLocalDb, getDataFromLocalDb, insertDataIntoLocalDB } from "../../services/dbService";
import { prepareOrderReqBody } from "../../features/createSecondaryOrder/CreateSecondaryOrderService";
import { analytics, logLoadTrace, performance } from "../../../src/firebase";
import { OrderType } from "../../types/types";
import {
  customStyle,
  GenericModal,
  getGenericModalProps,
  initialGenericModalProps,
} from "../../features/viewOrder/ViewOrderUtils";
import CustomGenericModal from "../dms_common/CustomGenericModal";
import "./CheckoutSummary.css";
import ScreenOverlay from "../dms_common/ScreenOverlay";
import { ReactComponent as DiscountIcon } from "../../assets/image/svg/discount.svg";
import { logEvent } from "firebase/analytics";
import useShortcutKeys from "../dms_common/ShortcutKeys";
import dayjs from "dayjs";
import { getConfigData } from "../../services/configurationService";

const CheckoutSummary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {
    fetchPricingBySalesrep,
    directInvoicingEnabled
  } = ConfigDictionary;
  const query = useQuery();
  const outletId = query.get("outletId") || "null";
  const orderType = (query.get("orderType") as OrderType) || OrderType.SECONDARY;
  const salesRep = query.has("salesRep") ? query.get("salesRep") : null;
  const beatQuery = query.has("beat") ? query.get("beat") : null;
  const locationState: any = location.state as any;
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [submitOrderLoading, setSubmitOrderLoading] = useState<boolean>(false);
  const [genericModalProps, setGenericModalProps] =
    useState<GenericModal>(initialGenericModalProps);
  const [ratingLoading, setRatingLoading] = useState<boolean>(false)

  const orderValidateRes = locationState?.orderValidateRes?.[0] || {};
  const isCounterSaleOrder = locationState?.isCounterSaleOrder ?? false;
  const outletReferenceId = locationState?.outletReferenceId ?? "";

  const handleSubmit = async () => {
    if (directInvoicingEnabled) {
      await handleSubmitInvoiceLog();
    } else {
      await handleSubmitOrderLog();
    }
  }

  const handleSubmitInvoiceLog = async() => {
      logLoadTrace(handleSubmitInvoice,"create_secondary_order_sumit_invoice_time");
  }

  const handleSubmitInvoice = async () => {
    setSubmitOrderLoading(true);
    const orderReqBody = prepareOrderReqBody(JSON.parse(JSON.stringify(orderValidateRes)), orderType, locationState, directInvoicingEnabled);
    try {
      const response = await ChannelkartNetworkPost("/v3/orders", orderReqBody);
      if (response?.status !== 200 && response?.status !== 201) {
        logEvent(analytics, "secondary_invoice_failed", {
          "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
          "error_reason": response ?? null,  // Assuming you want to log the whole response in case of an error
          "invoice_value": response?.data?.feature?.[0]?.totalAmount ?? null,
          "outlet_code": response?.data?.feature?.[0]?.outletCode ?? null,
          "sku_count": response?.data?.feature?.[0]?.lineCount ?? null,
        });

        setGenericModalProps(
          getGenericModalProps("error", "There is some issue while creating invoice")
        );
      } else {
        const res = await getDataFromLocalDb("Cart");
        const outletCode = response?.data?.features[0]?.outletCode;
        const outletData = res?.data?.[0]?.data?.[outletCode];
        const paymentMethod = outletData?.customer?.paymentMethod;

        const existingOutletList = await getDataFromLocalDb("OutletList");
        const index = existingOutletList.data.findIndex((item: any) => item.outletCode === outletCode);

        if (index > -1) {
          existingOutletList.data[index] = {
            ...existingOutletList.data[index],
            lastOrderDate: new Date().toISOString(),
          };

          await clearAllDataFromLocalDb("OutletList");
          await insertDataIntoLocalDB("OutletList", existingOutletList.data);
        }

        logEvent(analytics, "secondary_invoice_submitted", {
          "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
          "invoice_id": response?.data?.feature?.[0]?.id ?? null,
          "invoice_value": response?.data?.feature?.[0]?.totalAmount ?? null,
          "outlet_code": outletCode,
          "sku_count": response?.data?.feature?.[0]?.lineCount ?? null,
          "total_sku_normalised_qty": response?.data?.feature?.[0]?.normalizedQuantity ?? null,
          "payment_mode": paymentMethod,
          "discount_value": response?.data?.feature?.[0]?.discountInfo?.[0]?.discount ?? null,
          "sale_type": response?.data?.feature?.[0]?.type ?? null,
        });

        setOpen(true);
      }
    } catch (error) {
      logEvent(analytics, "secondary_invoice_failed", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "error_reason": error ?? null,  // Ensure that error is logged safely
        "invoice_value": orderReqBody?.totalAmount ?? null,
        "outlet_code": orderReqBody?.outletCode ?? null,
        "sku_count": orderReqBody?.lineCount ?? null,
      });

      console.error(error);
      setGenericModalProps(
        getGenericModalProps("error", "There is some issue while placing order")
      );
    } finally {
      setSubmitOrderLoading(false);
    }
  };

  const handleSubmitOrderLog = async() => {
      logLoadTrace(handleSubmitOrder,"create_secondary_order_sumit_order_time");
  }

  const handleSubmitOrder = async () => {
    setSubmitOrderLoading(true);
    const orderReqBody = prepareOrderReqBody(JSON.parse(JSON.stringify(orderValidateRes)), orderType, locationState);
    try {
      const response = await ChannelkartNetworkPost("/orders", orderReqBody);
      if (response?.status !== 200 && response?.status !== 201) {
        logEvent(analytics, "secondary_order_failed", {
          "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
          "error_reason": response ?? null,  // Assuming you want to log the whole response in case of an error
          "order_value": response?.data?.feature?.[0]?.totalAmount ?? null,
          "outlet_code": response?.data?.feature?.[0]?.outletCode ?? null,
          "sku_count": response?.data?.feature?.[0]?.lineCount ?? null,
        });

        setGenericModalProps(
          getGenericModalProps("error", "There is some issue while placing order")
        );
      } else {
        const res = await getDataFromLocalDb("Cart");
        const outletCode = response?.data?.feature[0]?.outletCode;
        const outletData = res?.data?.[0]?.data?.[outletCode];
        const paymentMethod = outletData?.customer?.paymentMethod;

        const existingOutletList = await getDataFromLocalDb("OutletList");
        const index = existingOutletList.data.findIndex((item: any) => item.outletCode === outletCode);

        if (index > -1) {
          existingOutletList.data[index] = {
            ...existingOutletList.data[index],
            lastOrderDate: new Date().toISOString(),
          };

          await clearAllDataFromLocalDb("OutletList");
          await insertDataIntoLocalDB("OutletList", existingOutletList.data);
        }

        logEvent(analytics, "secondary_order_submitted", {
          "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
          "order_id": response?.data?.feature?.[0]?.id ?? null,
          "order_value": response?.data?.feature?.[0]?.totalAmount ?? null,
          "outlet_code": outletCode,
          "sku_count": response?.data?.feature?.[0]?.lineCount ?? null,
          "total_sku_normalised_qty": response?.data?.feature?.[0]?.normalizedQuantity ?? null,
          "payment_mode": paymentMethod,
          "discount_value": response?.data?.feature?.[0]?.discountInfo?.[0]?.discount ?? null,
          "sale_type": response?.data?.feature?.[0]?.type ?? null,
        });

        setOpen(true);
      }
    } catch (error) {
      logEvent(analytics, "secondary_order_failed", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "error_reason": error ?? null,  // Ensure that error is logged safely
        "order_value": orderReqBody?.totalAmount ?? null,
        "outlet_code": orderReqBody?.outletCode ?? null,
        "sku_count": orderReqBody?.lineCount ?? null,
      });

      console.error(error);
      setGenericModalProps(
        getGenericModalProps("error", "There is some issue while placing order")
      );
    } finally {
      setSubmitOrderLoading(false);
    }
  };

  const handleStepperBack = () => {
    navigate(location.pathname.replace(/\/([^/]*)$/, `?outletId=${outletId}${(fetchPricingBySalesrep && fetchPricingBySalesrep) ? `&salesRep=${salesRep}&beat=${beatQuery}` : ''}`),{
        state: {
          isCounterSaleOrder: isCounterSaleOrder,
          outletReferenceId: outletReferenceId,
          showListView: locationState?.showListView
        },
      });
  };

  const handleNavigateToCatalogue = () => {
    if(orderType === OrderType.SECONDARY) {
    navigate(`/dashboard/sale/createSecondaryOrder?outletId=${outletId}${(fetchPricingBySalesrep && fetchPricingBySalesrep) ? `&salesRep=${salesRep}&beat=${beatQuery}` : ''}`, {
      state: {
        showListView: locationState?.showListView
      },
    });
    }else if(orderType === OrderType.PRIMARY){
    navigate(`/dashboard/purchase/createPrimaryOrder`);
    }
  }

  const clearCartDataFromLocalDB = async () => {
    const res = await getDataFromLocalDb("Cart");
    const localDbCartData = res?.data?.[0]?.data || {};
    const updatedCartData = {
      ...localDbCartData,
    };
    delete updatedCartData[outletId];
    await insertDataIntoLocalDB("Cart", [
      {
        id: 1,
        data: updatedCartData,
      },
    ]);
  };

  const handleCompletion = async () => {
    setRatingLoading(true)
    try {
      setOpen(false);
      clearCartDataFromLocalDB();

      orderType === OrderType.PRIMARY
      ? navigate(`/dashboard/purchase/viewPrimaryOrders`)
      : navigate(`/dashboard/sale/manageOrders`);


    } catch (e) {
      console.log(e);
    } finally {
      setRatingLoading(false)
    }
  }

  const totalAmountAndQtyInfo = {
    numberOfProducts: orderValidateRes?.lineCount,
    totalCaseQuantity: orderValidateRes?.totalCaseQuantity,
    totalPieceQuantity: orderValidateRes?.totalPieceQuantity,
    totalOtherUnitQuantity: orderValidateRes?.totalOtherUnitQuantity,
    totalAmount: orderValidateRes?.totalAmount,
    netAmount: orderValidateRes?.netAmount
  };

  const distributorDiscountInfo = orderValidateRes?.discountInfo?.find((discount: any) => discount?.id?.toLowerCase() === "cashdiscount") ?? {};

 useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+A (Windows/Linux) or Cmd+A (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
        event.preventDefault(); // Prevent default select all behavior
        
        // If success modal is open, trigger Continue button
        if (open && !ratingLoading) {
          handleCompletion();
        }
        // If no modals are open and not loading, trigger Submit Order
        else if (!submitOrderLoading && !ratingLoading && !open && !genericModalProps.open) {
          handleSubmitOrderLog();
        }
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [submitOrderLoading, ratingLoading, open, genericModalProps.open]);
const { positiveCta, negativeCta } = useShortcutKeys();
  return (
    <div className="CheckoutSummary_container">
      <ScreenOverlay apply={submitOrderLoading || ratingLoading} />

      <CustomGenericModal
        type={genericModalProps?.type}
        message={genericModalProps?.message}
        modalOpen={genericModalProps?.open}
        onClose={() => {
          setGenericModalProps(initialGenericModalProps);
          handleStepperBack();
        }}
      />

      <Modal open={open} onClose={() => {}} className="successful_message_pop">
        <Box className="successful_message_box">
          <img src={success} alt="Success" className="successful_message_img" />
          <Typography className="successful_message_heading">Successful!</Typography>
          <Typography className="successful_message_subtitle">
            {
              directInvoicingEnabled ? "Your invoice is successfully created." : "Your order is successfully placed."
            }
          </Typography>
          {/* <Box className="successful_message_rating">
            <Rating
              name="simple-controlled"
              value={rating}
              onChange={(event, newValue) => setRating(newValue)}
              precision={1}
              className="custom_rating"
            />
            <Typography className="successful_rating_message">How was everything?</Typography>
          </Box> */}
          <Button
            variant="contained"
            onClick={handleCompletion}
            className="successful_message_button"
            sx={{
              minWidth: "130px",
              minHeight: "37px",
              boxShadow: "none",
            }}
          >
            {ratingLoading ? <CircularProgress style={customStyle} /> : "Continue"}
            {!ratingLoading && <span style={{fontSize:"9.5px"}}>&nbsp;{positiveCta}</span>}
          </Button>
        </Box>
      </Modal>

      <FormHeader
        parentLabel={"Place New Order"}
        label={"Check out"}
        goBackToParent={handleStepperBack}
        childLabel={"Order Summary"}
        childLableCaseClick={handleNavigateToCatalogue}
      />

      <Grid container spacing={2} className="grid_container" sx={{ height: "86%", marginTop: "0" }}>
        <Grid item xs={5} sx={{ height: "100%" }}>
          <div style={{height: "100%", overflow: "auto", scrollbarWidth: "none", msOverflowStyle: "none"}}>
          <OrderSummary
            title={"Order Summary"}
            outletCode={orderValidateRes?.outletCode || 0}
            outletName={orderValidateRes?.outletName || 0}
            billDate={locationState?.billDate}
            billToAddress={locationState?.billToAddress}
            deliveryDate={locationState?.deliveryDate}
            shipToAddress={locationState?.shipToAddress}
            salesRep={locationState?.salesRep || ""}
            salesType={locationState?.salesType || ""}
            customerChannel={locationState?.customerChannel || ""}
            paymentMethod={locationState?.paymentMethod || ""}
            totalAmount={orderValidateRes?.totalAmount || 0}
            finalAmount={orderValidateRes?.netAmount || 0}
            couponDiscount={orderValidateRes?.couponValue || 0}
            schemeDiscount={orderValidateRes?.discountValue || 0}
            taxAmount={orderValidateRes?.totalTaxAmount || 0}
            distributorDiscountInfo={distributorDiscountInfo}
          />
          {(orderValidateRes?.discountInfo?.filter((x: any) => x?.id?.toLowerCase() !== "cashdiscount")?.length > 1) && <div className="order_summary_container" style={{ marginTop: "0.7rem", padding: 0 }}>
            <div className="sub_header">
              <div
                className="confirmed_order_icon"
                style={{ display: "flex", alignItems: "center" }}
              >
                <PercentIcon width={22} height={22} />
              </div>
              <Typography sx={{ fontWeight: 500, fontSize: "16px !important", color: "#227C9D" }}>
                Discounts Applicable
              </Typography>
            </div>
            <div className="checkout_summary_discounts_box">
              <div style={{ color: "#00C6B1", display: "flex", alignItems: "center" }}>
                <DiscountIcon width={18} height={18} />
                <span style={{fontSize: "14px", fontWeight: 500, marginLeft: "6px"}}>Coupons</span>
              </div>
              <div>
                {orderValidateRes?.discountInfo?.map((discount: any) => {
                  return (
                    !["summary"].includes(discount?.discountType?.toLowerCase()) && (
                      <p key={discount?.id} style={{fontSize: "13px", fontWeight: 450, lineHeight: "1.3", paddingTop: "8px"}}>{discount?.discountInfo ?? ""}</p>
                    )
                  );
                })}
              </div>
            </div>
          </div>}
          </div>
        </Grid>
        <Grid item xs={7} sx={{ height: "100%" }}>
          <OrderDetails
            title = {"Order Details"}
            orderDetails={orderValidateRes?.orderDetails || []}
            skuCodeSortOrder={locationState?.skuCodeSortOrder}
          />
        </Grid>
      </Grid>
      <div className="Checkout_cartSummaryFooter_container_wrapper">
        <div className="Checkout_cartSummaryFooter_container">
          <CartSummaryFooter
            cartProducts={null}
            onButtonClick={handleSubmit}
            buttonText={directInvoicingEnabled ? "Submit Invoice" : "Submit Order"}
            totalAmountAndQtyInfo={totalAmountAndQtyInfo}
            loading={submitOrderLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default CheckoutSummary;
